import { useContext } from 'react';
import { TagContext } from 'app/state/contexts/TagContext';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { i18n } from 'app/utils/i18n';
import { getTagDotColor } from '../utils/colors';
import { useBlockTagColors } from '../utils/useBlockTagColors';
import { TagBinding } from '../types/block';

interface BlockTagBadgeProps {
  bindings: TagBinding[];
  onContinueClick: (firstTagId: string) => void;
  onSelectTag?: (tagId: string) => void;
  tooltipKey?: string;
  canEdit?: boolean;
}

export const BlockTagBadge = ({ bindings, onContinueClick, onSelectTag, tooltipKey, canEdit = true }: BlockTagBadgeProps) => {
  const { tagDataMap, highlightedOption } = useContext(TagContext) ?? {};
  const { taggingSubMode } = useEditorMode();
  const colors = useBlockTagColors(bindings);

  if (!bindings || bindings.length === 0) return null;

  const isCoverageMode = taggingSubMode === 'mark-coverage';

  const firstBinding = bindings[0];
  const firstTagData = tagDataMap?.[firstBinding.tagId];
  const hasContinuation = !!firstBinding.continuedAt || !!firstTagData?.nextSegment;
  const isContinuation = !!firstTagData?.predecessorId;
  const arrowColor = colors?.indicator ?? '#62a4b9';

  return (
    <div
      className="editor-image--tag-badge"
      style={{
        backgroundColor: colors?.highlight ?? '#add8e6',
        borderColor: colors?.indicator ?? '#62a4b9',
      }}
      contentEditable={false}
    >
      {isContinuation && <i className="fa-solid fa-arrow-left fa-xs" style={{ fontFamily: 'Font Awesome 6 Pro', color: arrowColor }} />}
      <i className="fa-regular fa-tag" style={{ fontFamily: 'Font Awesome 6 Pro' }} title={i18n(tooltipKey ?? 'tag.block.image.tagged')} />
      {hasContinuation && <i className="fa-solid fa-arrow-right fa-xs" style={{ fontFamily: 'Font Awesome 6 Pro', color: arrowColor }} />}
      {bindings.length > 1 ? (
        onSelectTag ? (
          <span className="editor-image--tag-badge__dots" contentEditable={false}>
            {bindings.map((b) => (
              <span
                key={b.tagId}
                className="editor-image--tag-badge__dot"
                title={tagDataMap?.[b.tagId]?.concept?.name ?? undefined}
                style={{ backgroundColor: getTagDotColor(highlightedOption, tagDataMap?.[b.tagId]) }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectTag(b.tagId);
                }}
              />
            ))}
          </span>
        ) : (
          <span className="editor-image--tag-badge__count">{bindings.length}</span>
        )
      ) : null}
      {canEdit && !isCoverageMode && (
        <button
          type="button"
          className="editor-image--tag-badge__continue"
          title={i18n('tag.block.image.continue')}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onContinueClick(firstBinding.tagId);
          }}
        >
          <i className="fa-regular fa-plus" style={{ fontFamily: 'Font Awesome 6 Pro' }} />
        </button>
      )}
    </div>
  );
};
